import { useState } from "react";

export type RevenueCmMode = "revenue" | "profit";

/** Keep each card's dollar basis stable between visits. A blocked browser may
 * reject localStorage; the card still works for the current visit in that case. */
export function useStickyRevenueCmMode(
  key: string,
  defaultMode: RevenueCmMode = "revenue",
): [RevenueCmMode, (mode: RevenueCmMode) => void] {
  const [mode, setMode] = useState<RevenueCmMode>(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved === "profit" || saved === "cm" ? "profit" : defaultMode;
    } catch {
      return defaultMode;
    }
  });

  const set = (next: RevenueCmMode): void => {
    setMode(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // Persistence is optional. The visible card selection still changes.
    }
  };

  return [mode, set];
}

export function RevenueCmToggle({
  mode,
  setMode,
  cmDisabled = false,
}: {
  mode: RevenueCmMode;
  setMode: (mode: RevenueCmMode) => void;
  cmDisabled?: boolean;
}): React.ReactElement | null {
  // Pass 1 is intentionally revenue-only. Repeating a disabled CM button on
  // every card turns one unfinished expense setup into a wall of nags. The
  // control appears only when the account-wide expense review says CM is safe.
  if (cmDisabled) return null;

  return (
    <div
      aria-label="Dollar basis"
      onClick={(event) => event.stopPropagation()}
      style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 999, overflow: "hidden" }}
      title="Show revenue or contribution margin"
    >
      {(["revenue", "profit"] as const).map((option) => {
        const disabled = false;
        const active = mode === option;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => { if (!disabled) setMode(option); }}
            style={{
              border: "none",
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.4 : 1,
              background: active ? "var(--accent)" : "transparent",
              color: active ? "var(--accent-contrast)" : "var(--body)",
            }}
          >
            {option === "revenue" ? "Revenue" : "CM"}
          </button>
        );
      })}
    </div>
  );
}
